import React, { useEffect, useMemo, useState } from "react";
import ResetButton from "@/components/ResetButton";
import useDraft from "@/lib/useDraft";
import { Layers, Flag } from "lucide-react";
import { api } from "@/lib/api";
import { useRate } from "@/context/RateContext";
import { Film, Wand2, Move, Volume2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import PromptBox, { renumberTags, dropTag } from "@/components/PromptBox";
import { toast } from "sonner";
import ModelPicker, { ModelIcon } from "@/components/ModelPicker";
import GenerationGrid from "@/components/GenerationGrid";
import GenerateButton, { useCost } from "@/components/GenerateButton";
import UploadZone from "@/components/UploadZone";
import { Seg, Field } from "@/components/Seg";
import { useAttachments } from "@/lib/useUploader";
import VirtualTrim from "@/components/VirtualTrim";

const TABS = [
  { id: "create", label: "Create Video", icon: Film },
  { id: "edit", label: "Edit Video", icon: Wand2 },
  { id: "motion", label: "Motion Control", icon: Move },
];
const RES = ["480p", "720p", "1080p"];
const ASPECTS = ["auto", "16:9", "9:16", "1:1", "4:3", "3:4", "21:9"];

const groupsFor = (mode) => [
  { label: "Featured", filter: (m) => m.featured && m.section === "video" && m.modes.includes(mode) },
  { label: "Video Models", filter: (m) => m.section === "video" && m.modes.includes(mode) },
];

export default function VideoStudio({ projectId, models, configured, items, references, onUploaded, generate, onOpen, onRetry, seed , header, onChanged, modelPick, onReset}) {
  const [tab, setTab] = useState("create");
  const [model, setModel] = useState(null);
  const [prompt, setPrompt] = useDraft(`ax_draft_${projectId}_video`);
  const [duration, setDuration] = useState(8);
  const [aspect, setAspect] = useState("16:9");
  const [resolution, setResolution] = useState("720p");
  const [quality, setQuality] = useState("Standard");
  const [sound, setSound] = useState(true);
  const [busy, setBusy] = useState(false);
  const [frames, setFrames] = useState(false);
  const [batchOn, setBatchOn] = useState(false);
  const [batch, setBatch] = useState([]);
  const [batchUsd, setBatchUsd] = useState({});
  const { rate } = useRate();
  const startF = useAttachments(projectId, 1, onUploaded, true, "video-startF");
  const endF = useAttachments(projectId, 1, onUploaded, true, "video-endF");
  const refs = useAttachments(projectId, 10, onUploaded, true, "video-refs");
  const source = useAttachments(projectId, 1, onUploaded, true, "video-source");
  const audio = useAttachments(projectId, 1, onUploaded, true, "video-audio");
  const face = useAttachments(projectId, 8, onUploaded, true, "video-face");
  const refVid = useAttachments(projectId, 1, onUploaded, true, "video-refVid");

  const pool = useMemo(() => models.filter((m) => m.section === "video" && m.modes.includes(tab)), [models, tab]);
  useEffect(() => {
    setModel((cur) => (cur && pool.some((m) => m.id === cur.id) ? cur : pool.find((m) => m.available && m.featured) || pool.find((m) => m.available) || pool[0] || null));
  }, [pool]);
  const picked = React.useRef(null);
  useEffect(() => {
    const m = modelPick && models.find((x) => x.id === modelPick.id && x.section === "video");
    if (!m) return;
    picked.current = m;
    if (!m.modes.includes(tab)) setTab(m.modes[0]); else setModel(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelPick, models]);
  useEffect(() => { if (picked.current && pool.some((x) => x.id === picked.current.id)) { setModel(picked.current); picked.current = null; } }, [pool]);

  useEffect(() => {
    if (!seed) return;
    const create = !(seed.mode && TABS.some((t) => t.id === seed.mode)) || seed.mode === "create";
    setTab(create ? "create" : seed.mode);
    setPrompt(seed.prompt || "");
    const s = seed.settings || {};
    if (s.duration) setDuration(s.duration);
    if (s.aspect_ratio) setAspect(s.aspect_ratio);
    if (s.resolution) setResolution(s.resolution);
    if (typeof s.generate_audio === "boolean") setSound(s.generate_audio);
    const pick = (ids) => (ids || []).map((id) => references.find((r) => r.id === id)).filter(Boolean);
    const imgs = pick(seed.image_ids), vid = pick([seed.video_id]), aud = pick([seed.audio_id]);
    [startF, endF, refs, source, audio, face, refVid].forEach((b) => b.setItems([]));
    if (create) {
      const end = pick([seed.end_image_id]);
      setFrames(!!end.length);
      if (end.length) { startF.setItems(imgs.slice(0, 1)); endF.setItems(end); } else refs.setItems(imgs);
      refVid.setItems(vid);
    } else { face.setItems(imgs); source.setItems(vid); audio.setItems(aud); }
    const m = models.find((x) => x.id === seed.model_id && x.section === "video");
    if (m) setTimeout(() => setModel(m), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, models]);

  const caps = model?.capabilities || {};
  const cons = (model?.constraints || []).join(" ").toLowerCase();
  const rng = caps.duration_seconds || {};
  const listed = (cons.match(/duration (\d+(?:, \d+)*) or (\d+)/) || []).slice(1).join(",").split(/,\s*/).filter(Boolean).map(Number);
  const dMin = listed[0] || rng.min || 2;
  const dMax = listed[listed.length - 1] || rng.max || 30;
  const dStep = listed.length > 1 ? listed[1] - listed[0] : 1;
  const clampDur = (d) => Math.min(dMax, Math.max(dMin, dMin + Math.round((d - dMin) / dStep) * dStep));
  const okRes = (r) => (caps.resolutions || []).includes(r);
  const aspects = (caps.aspect_ratios || []).filter((a) => ASPECTS.includes(a));
  const isLipsync = !!caps.lipsync;
  const needsVideo = tab !== "create" && (caps.video_to_video || tab === "motion");
  const needsFace = tab === "motion" || (isLipsync && caps.image_to_video);
  const needsAudio = tab === "edit" && isLipsync;
  const promptOff = cons.includes("no prompt");
  const imageOnly = tab === "create" && caps.image_to_video && !caps.text_to_video;

  useEffect(() => {
    if (!model) return;
    setDuration((d) => clampDur(d));
    if (!okRes(resolution) && caps.resolutions?.length) setResolution(RES.find(okRes) || caps.resolutions[0]);
    if (aspects.length && !aspects.includes(aspect)) setAspect(aspects.includes("16:9") ? "16:9" : aspects[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const useFrames = tab === "create" && frames && caps.end_frame;
  const trimSrc = needsVideo ? source.items[0] : tab === "create" && !useFrames && caps.reference_videos ? refVid.items[0] : null;
  const trimId = trimSrc?.id || null;
  const [vinfo, setVinfo] = useState(null);
  const [vtrim, setVtrim] = useState(null);
  useEffect(() => {
    setVinfo(null); setVtrim(null);
    if (trimId) api.get(`/references/${trimId}/info`).then((r) => setVinfo(r.data)).catch(() => {});
  }, [trimId]);
  const trimSecs = vtrim && vinfo?.fps ? (vtrim[1] - vtrim[0] + 1) / vinfo.fps : null;
  const mediaDur = (needsVideo && (trimSecs || source.items[0]?.duration)) || (needsAudio && audio.items[0]?.duration) || null;
  const settings = {
    duration: tab === "create" ? clampDur(duration) : Math.ceil(mediaDur || rng.min || 5),
    ...(okRes(resolution) && { resolution }),
    ...(tab === "create" && aspects.includes(aspect) && { aspect_ratio: aspect }),
    ...(caps.audio && !isLipsync && tab === "create" && { generate_audio: sound }),
    ...(vtrim && trimId && { vtrim }),
  };
  const mainUsd = useCost(model?.available && model.id, settings);
  const uploading = refs.uploading || source.uploading || audio.uploading || face.uploading || startF.uploading || endF.uploading;

  useEffect(() => { setBatch((b) => b.filter((id) => id !== model?.id)); }, [model]);
  const createModels = useMemo(() => models.filter((m) => m.section === "video" && m.modes.includes("create") && m.available), [models]);
  const extras = batchOn && tab === "create" ? batch.filter((id) => id !== model?.id).map((id) => createModels.find((m) => m.id === id)).filter(Boolean) : [];
  const fitFor = (m) => {
    const c = m.capabilities || {};
    const r = c.duration_seconds || {};
    const res = (c.resolutions || []).includes(resolution) ? resolution : (c.resolutions || []).includes("720p") ? "720p" : (c.resolutions || [])[0];
    return {
      duration: Math.min(r.max || 30, Math.max(r.min || 2, clampDur(duration))),
      ...(res && { resolution: res }),
      ...((c.aspect_ratios || []).includes(aspect) && { aspect_ratio: aspect }),
      ...(c.audio && { generate_audio: sound }),
    };
  };
  const extrasKey = JSON.stringify(extras.map((m) => [m.id, fitFor(m)]));
  useEffect(() => {
    Promise.all(extras.map((m) => api.post("/models/cost", { model_id: m.id, settings: fitFor(m) }).then((r) => [m.id, r.data.usd]).catch(() => [m.id, 0])))
      .then((pairs) => setBatchUsd(Object.fromEntries(pairs)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extrasKey]);
  const usd = mainUsd == null ? null : mainUsd + extras.reduce((a, m) => a + (batchUsd[m.id] || 0), 0);

  const reason = !configured ? "Coming Soon" : !model?.available ? "This model isn't available yet"
    : uploading ? "Waiting for upload…"
    : needsVideo && !source.ids.length ? "Add a source video"
    : needsFace && !face.ids.length ? (tab === "motion" ? "Add a character photo" : "Add a face photo")
    : needsAudio && !audio.ids.length ? "Add an audio track"
    : useFrames && (!startF.ids.length || !endF.ids.length) ? "Add both a start and an end frame"
    : imageOnly && !useFrames && !refs.ids.length ? `${model.name} needs a starting image`
    : prompt.length > (model?.max_prompt || 1e9) ? `Prompt too long — max ${model.max_prompt} characters`
    : extras.find((m) => prompt.length > (m.max_prompt || 1e9)) ? `Prompt too long for ${extras.find((m) => prompt.length > (m.max_prompt || 1e9)).name} (max ${extras.find((m) => prompt.length > (m.max_prompt || 1e9)).max_prompt}) — shorten it or untick that model`
    : extras.find((m) => m.capabilities?.image_to_video && !m.capabilities?.text_to_video && !refs.ids.length && !startF.ids.length) ? `${extras.find((m) => m.capabilities?.image_to_video && !m.capabilities?.text_to_video).name} needs a starting image`
    : !promptOff && tab === "create" && !prompt.trim() ? "Write a prompt first" : "";

  const refMax = caps.reference_images ? 10 : 1;
  const faceMax = tab === "motion" ? 8 : 1;
  // Friendly @image1 / @video1 / @audio1 tags for attached media only; converted to the model's wire tags on send.
  const promptTags = useMemo(() => {
    const list = (bag, kind, from = 0) => bag.items.map((r, i) => ({ tag: `${kind}${from + i + 1}`, ref: r }));
    if (tab === "create") return useFrames ? [...list(startF, "image"), ...list(endF, "image", startF.items.length)] : [...list(refs, "image"), ...list(refVid, "video")];
    return [...list(source, "video"), ...list(face, "image"), ...list(audio, "audio")];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, useFrames, refs.items, refVid.items, source.items, face.items, audio.items, startF.items, endF.items]);
  const imgCap = caps.image_to_video || caps.reference_images ? refMax : 0;
  const tagWarn = tab === "create" && !useFrames && (refs.items.length > imgCap || (refVid.items.length && !caps.reference_videos))
    ? `${model?.name} takes ${imgCap ? `up to ${imgCap} image${imgCap > 1 ? "s" : ""}` : "no images"}${caps.reference_videos ? "" : " and no reference video"} — extra attachments will be ignored` : "";
  const toWire = (p) => (tab === "create"
    ? p.replace(/@image_?(\d+)\b/g, "@image_$1").replace(/@video_?(\d+)\b/g, "@video_$1")
    : p.replace(/@image_?(\d+)\b/g, "@image_$1").replace(/@video_?\d+\b/g, "@video").replace(/@audio_?\d+\b/g, "@audio"));

  const createPayload = (m, st) => {
    const c = m.capabilities || {};
    const canImg = c.image_to_video || c.reference_images;
    const max = c.reference_images ? 10 : 1;
    const withFrames = frames && c.end_frame && startF.ids.length && endF.ids.length;
    const p = toWire(prompt);
    return {
      section: "video", mode: "create", model_id: m.id, settings: { ...st, ...(vtrim && !withFrames && c.reference_videos ? { vtrim } : {}) },
      prompt: canImg || withFrames ? p : p.replace(/@image_\d+\b ?/g, "").trim(),
      image_ids: withFrames ? startF.ids : canImg ? refs.ids.slice(0, max) : (startF.ids.length && c.image_to_video ? startF.ids : []),
      end_image_id: withFrames ? endF.ids[0] : null,
      video_id: !withFrames && c.reference_videos ? refVid.ids[0] || null : null,
    };
  };

  const go = async () => {
    setBusy(true);
    if (tab === "create") {
      const batchId = extras.length ? `b_${Date.now()}` : null;
      const jobs = [createPayload(model, settings), ...extras.map((m) => createPayload(m, fitFor(m)))];
      await Promise.all(jobs.map((j) => generate({ ...j, batch_id: batchId })));
    } else {
      await generate({
        section: "video", mode: tab, model_id: model.id, settings, prompt: promptOff ? "" : toWire(prompt),
        image_ids: needsFace ? face.ids.slice(0, faceMax) : [],
        video_id: needsVideo ? source.ids[0] || null : (tab === "create" && !useFrames && caps.reference_videos ? refVid.ids[0] || null : null), audio_id: needsAudio ? audio.ids[0] || null : null,
        media_duration: needsVideo || needsAudio ? mediaDur : null,
      });
    }
    setBusy(false);
  };

  return (
    <div className="flex-1 flex min-h-0">
      <aside className="w-[380px] shrink-0 border-r border-white/5 bg-[#0B1013]/70 backdrop-blur-xl flex flex-col min-h-0" data-testid="video-sidebar">
        <div className="px-3 pt-3 flex justify-end"><ResetButton onReset={onReset} testid="video-panel-reset" /></div>
        <div className="p-3 grid grid-cols-3 gap-1 border-b border-zinc-800/80">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`video-tab-${t.id}`}
              className={`flex flex-col items-center gap-1 py-2 rounded-lg text-[11px] font-medium transition-colors ${tab === t.id ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-white hover:bg-zinc-800/50"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          <div className="ax-panel p-3 flex items-center gap-3" data-testid="video-model-card">
            <span className="ax-model-icon h-9 w-9"><ModelIcon icon={model?.icon} /></span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="video-model-name">{model?.name || "No model"}</div>
              <div className="text-[11px] text-zinc-500 line-clamp-2">{model?.description}</div>
            </div>
            <ModelPicker models={models} value={model?.id} onChange={setModel} configured={configured} groups={groupsFor(tab)} testid="video-model" changeButton />
          </div>

          {tab === "create" && caps.end_frame && (
            <label className="flex items-center justify-between ax-panel px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm"><Flag className="w-4 h-4 text-zinc-400" />Start &amp; end frames
                <span className="text-[11px] text-zinc-500">control how the shot begins and ends</span></span>
              <Switch className="data-[state=checked]:bg-[#7AF25F]" checked={frames} onCheckedChange={setFrames} data-testid="video-frames-switch" />
            </label>
          )}
          {useFrames && (
            <div className="grid grid-cols-2 gap-2" data-testid="video-frames">
              <UploadZone testid="video-start-frame" title="Start frame" badge="Required" subtitle="First frame of the clip" accept="image/*" max={1}
                items={startF.items} onFiles={startF.add} onRemove={startF.remove} />
              <UploadZone testid="video-end-frame" title="End frame" badge="Required" subtitle="Last frame of the clip" accept="image/*" max={1}
                items={endF.items} onFiles={endF.add} onRemove={endF.remove} />
            </div>
          )}
          {tab === "create" && !useFrames && (
            <UploadZone testid="video-reference-zone" title="Add reference media" badge={imageOnly ? "Required" : "Optional"}
              subtitle={caps.reference_images ? "1 image = first frame · 2+ images = character/style references" : caps.image_to_video ? "Used as the first frame" : "This model is text-only"}
              accept="image/*" max={refMax} items={caps.image_to_video || caps.reference_images ? refs.items.slice(0, refMax) : []} onFiles={refs.add} onRemove={(i) => { refs.remove(i); setPrompt((p) => renumberTags(p, i, "image", "")); }}
              disabled={!caps.image_to_video && !caps.reference_images} />
          )}
          {tab === "create" && !useFrames && caps.reference_videos && (
            <UploadZone testid="video-reference-video-zone" title="Add reference video" badge="Optional · ≤ 30 s"
              subtitle="Motion, camera or style reference — used together with your reference images" accept="video/*" max={1}
              items={refVid.items} onFiles={refVid.add} onRemove={(i) => { refVid.remove(i); setPrompt((p) => dropTag(dropTag(p, "video1"), "video_1")); }} />
          )}
          {needsVideo && (
            <UploadZone testid="video-source-zone" title={tab === "motion" ? "Add motion reference video" : "Add video to edit"} badge="Required"
              subtitle={`Length ${rng.min || 2}–${Math.min(rng.max || 30, tab === "motion" ? 15 : 60)} s · MP4/MOV`} accept="video/*" max={1}
              items={source.items} onFiles={source.add} onRemove={(i) => { source.remove(i); setPrompt((p) => dropTag(dropTag(p, "video1"), "video")); }} />
          )}
          {trimSrc && vinfo?.frames > 1 && (
            <VirtualTrim key={trimId} total={vinfo.frames} fps={vinfo.fps} value={vtrim} onChange={setVtrim} testid="video-vtrim"
              note="only this part is sent to the model and returned — your original file is untouched" />
          )}
          {needsFace && (
            <UploadZone testid="video-face-zone" title={tab === "motion" ? "Add character photo" : "Add face photo"} badge="Required"
              subtitle={tab === "motion" ? "1–8 clear photos of the character" : "A clear, front-facing portrait"} accept="image/*" max={faceMax}
              items={face.items.slice(0, faceMax)} onFiles={face.add} onRemove={(i) => { face.remove(i); setPrompt((p) => renumberTags(p, i, "image", "")); }} />
          )}
          {needsAudio && (
            <UploadZone testid="video-audio-zone" title="Add speech audio" badge="Required" subtitle="MP3/WAV — the video will match its length"
              accept="audio/*" max={1} items={audio.items} onFiles={audio.add} onRemove={(i) => { audio.remove(i); setPrompt((p) => dropTag(dropTag(p, "audio1"), "audio")); }} />
          )}

          {!promptOff && (
            <Field label={tab === "create" ? "Prompt" : "Prompt (optional)"}>
              <div className="ax-input p-0 px-3 py-2">
                <PromptBox value={prompt} onChange={setPrompt} testid="video-prompt" maxChars={model?.max_prompt} tags={promptTags} improve
                  placeholder={tab === "create" ? "Describe the shot: subject, action, camera, mood…  Type @ to tag attached media" : "Describe what to change…  Type @ to tag attached media"}
                  className="bg-transparent text-sm placeholder:text-zinc-600" />
                {tagWarn && <div className="text-[11px] text-amber-300 mt-1" data-testid="video-tag-warning">{tagWarn}</div>}
              </div>
            </Field>
          )}

          {tab === "create" && (
            <Field label="Duration" hint={`${model?.name || "This model"} supports ${dMin}–${dMax} s${dStep > 1 ? ` in ${dStep} s steps` : ""}`}>
              <div className="flex items-center gap-3" data-testid="video-duration">
                <span className="text-[11px] font-mono text-zinc-600 w-6">{dMin}s</span>
                <Slider value={[clampDur(duration)]} min={dMin} max={dMax} step={dStep} onValueChange={([v]) => setDuration(v)}
                  className="flex-1 [&_[role=slider]]:bg-[#7AF25F] [&_[role=slider]]:border-[#7AF25F] [&>span:first-child>span]:bg-gradient-to-r [&>span:first-child>span]:from-[#7AF25F] [&>span:first-child>span]:to-[#5FC0C8]"
                  data-testid="video-duration-slider" />
                <span className="text-[11px] font-mono text-zinc-600 w-6">{dMax}s</span>
                <span className="ax-chip h-8 min-w-[52px] justify-center font-mono text-white" data-testid="video-duration-value">{clampDur(duration)}s</span>
              </div>
            </Field>
          )}
          {tab === "create" && (
            <Field label="Aspect ratio">
              <Select value={aspects.includes(aspect) ? aspect : undefined} onValueChange={setAspect} disabled={!aspects.length}>
                <SelectTrigger className="ax-input h-10" data-testid="video-aspect-trigger"><SelectValue placeholder={aspects.length ? "Choose" : "Set by your image"} /></SelectTrigger>
                <SelectContent className="bg-[#121214] border-zinc-800 text-zinc-200">
                  {aspects.map((a) => <SelectItem key={a} value={a} data-testid={`video-aspect-${a.replace(":", "-")}`}>{a === "auto" ? "Auto" : a}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {caps.resolutions?.length > 0 && (
            <Seg label="Resolution" testid="video-resolution" options={caps.resolutions} value={resolution} onChange={setResolution} isEnabled={okRes}
              hint={`${model?.name} offers ${(caps.resolutions || []).join(", ")}`} />
          )}
          {tab === "create" && (
            <Seg label="Quality" testid="video-quality" options={["Standard", "High"]} value={quality} onChange={setQuality}
              isEnabled={(q) => q === "Standard"} hint="On BFX, quality comes from the resolution — pick 1080p for the sharpest result" />
          )}
          {tab === "create" && caps.audio && (
            <label className="flex items-center justify-between ax-panel px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm"><Volume2 className="w-4 h-4 text-zinc-400" />Native sound</span>
              <Switch className="data-[state=checked]:bg-[#7AF25F]" checked={sound} onCheckedChange={setSound} disabled={cons.includes("no generate_audio")} data-testid="video-sound-switch" />
            </label>
          )}
          {tab !== "create" && mediaDur ? <p className="text-[11px] text-zinc-500">Billed for {Math.ceil(mediaDur)} s — the length of your upload.</p> : null}
        </div>
        <div className="p-4 border-t border-zinc-800/80">
          {tab === "create" && (
            <div className="mb-3 ax-panel p-3 space-y-2" data-testid="video-batch">
              <label className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm"><Layers className="w-4 h-4 text-zinc-400" />Batch variations
                  <span className="text-[11px] text-zinc-500">same prompt on more models</span></span>
                <Switch className="data-[state=checked]:bg-[#7AF25F]" checked={batchOn} onCheckedChange={setBatchOn} data-testid="video-batch-switch" />
              </label>
              {batchOn && (
                <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                  {createModels.filter((m) => m.id !== model?.id).map((m) => {
                    const on = batch.includes(m.id);
                    return (
                      <button key={m.id} onClick={() => setBatch((b) => (on ? b.filter((x) => x !== m.id) : b.filter((x) => x !== model?.id).length >= 3 ? b : [...b, m.id]))}
                        data-testid={`video-batch-${m.id}`} title={on && batchUsd[m.id] != null ? `≈ ₹${(batchUsd[m.id] * rate).toFixed(2)}` : m.description}
                        className={`px-2.5 h-7 rounded-md text-[11px] border ${on ? "bg-zinc-100 text-black border-zinc-100" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>{m.name}</button>
                    );
                  })}
                </div>
              )}
              {batchOn && <p className="text-[11px] text-zinc-500">{extras.length ? `${extras.length + 1} videos will render at once — settings are adjusted to each model's limits.` : "Pick up to 3 extra models."}</p>}
            </div>
          )}
          <GenerateButton usd={usd} onClick={go} busy={busy} disabledReason={reason} testid="video-generate" confirm className="w-full" />
          {reason && <p className="text-[11px] text-zinc-500 text-center mt-2" data-testid="video-generate-hint">{reason}</p>}
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto p-6" data-testid="video-history">
        {header}
        <GenerationGrid items={items} onOpen={onOpen} onRetry={onRetry} onChanged={onChanged} cols="grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3"
          emptyTitle="No videos yet" emptyHint="Pick a model on the left, describe your shot and press Generate. Videos take 1–5 minutes; you can keep working while they render." />
      </main>
    </div>
  );
}
